/**
 * String-literal unions mirroring every CHECK constraint in the SQL schema.
 *
 * Each union is backed by a `const` tuple so both runtime validation
 * (`USER_ROLES.includes(x)`) and compile-time narrowing stay in sync with the
 * database. If a CHECK constraint changes, change it here in the same commit.
 */
/* -------------------------------------------------------------------------- */
/* core                                                                       */
/* -------------------------------------------------------------------------- */
/** users.role */
export const USER_ROLES = ['user', 'master', 'admin', 'support', 'finance', 'content'];
/**
 * Operator roles and what each may do in the panel. `admin` may do
 * everything; the other three are the desks of a small team: support sees
 * people and their verification, finance moves money and reads the books,
 * content runs the masters, signals and announcements. The server enforces
 * this; the panel only hides what a role cannot use.
 */
export const OPERATOR_ROLES = ['admin', 'support', 'finance', 'content'];
export const PERMISSIONS = [
    'overview',
    'users',
    'users.role',
    'kyc',
    'withdrawals',
    'registry',
    'masters',
    'signals',
    'referrals',
    'announcements',
    'reports',
    /** A user's ledger and manual corrections to it. */
    'ledger',
    'settings',
    'audit',
];
export const ROLE_PERMISSIONS = {
    admin: PERMISSIONS,
    support: ['overview', 'users', 'kyc', 'announcements', 'audit'],
    finance: ['overview', 'withdrawals', 'registry', 'referrals', 'reports', 'ledger', 'audit'],
    content: ['overview', 'masters', 'signals', 'announcements'],
};
export function isOperatorRole(role) {
    return OPERATOR_ROLES.includes(role);
}
export function hasPermission(role, permission) {
    return isOperatorRole(role) && ROLE_PERMISSIONS[role].includes(permission);
}
/** users.status */
/**
 * Interface languages, in the order the language screen lists them. The web
 * app ships a full dictionary for each; announcements are written per locale.
 */
export const LOCALES = ['ru', 'en', 'ka', 'fr', 'it', 'ro', 'pt', 'es', 'th', 'zh', 'de'];
/** Endonyms — the one form of a language list a person can read before the UI switches. */
export const LOCALE_NAMES = {
    ru: 'Русский',
    en: 'English',
    ka: 'ქართული',
    fr: 'Français',
    it: 'Italiano',
    ro: 'Română',
    pt: 'Português',
    es: 'Español',
    th: 'ไทย',
    // Mandarin in simplified characters, the written standard the endonym names.
    zh: '中文',
    de: 'Deutsch',
};
/** BCP-47 tags for `Intl` formatting: dates and times follow the language, amounts never do. */
export const LOCALE_TAGS = {
    ru: 'ru-RU',
    en: 'en-GB',
    ka: 'ka-GE',
    fr: 'fr-FR',
    it: 'it-IT',
    ro: 'ro-RO',
    pt: 'pt-PT',
    es: 'es-ES',
    th: 'th-TH',
    zh: 'zh-CN',
    de: 'de-DE',
};
export function isLocale(value) {
    return typeof value === 'string' && LOCALES.includes(value);
}
export const USER_STATUSES = ['active', 'suspended', 'banned'];
/** email_tokens.purpose */
export const EMAIL_TOKEN_PURPOSES = ['verify_email', 'reset_password', 'confirm_address'];
/* -------------------------------------------------------------------------- */
/* kyc                                                                        */
/* -------------------------------------------------------------------------- */
/** kyc_applications.status */
export const KYC_STATUSES = ['pending', 'in_review', 'approved', 'rejected', 'resubmit'];
/** kyc_applications.document_type */
export const KYC_DOCUMENT_TYPES = ['passport', 'id_card', 'driver_license'];
/** kyc_documents.kind */
export const KYC_DOCUMENT_KINDS = [
    'passport_main',
    'passport_registration',
    'selfie_with_document',
    'proof_of_address',
];
/* -------------------------------------------------------------------------- */
/* networks & assets                                                          */
/* -------------------------------------------------------------------------- */
/** networks.family */
export const NETWORK_FAMILIES = [
    'evm',
    'solana',
    'tron',
    'bitcoin',
    'cosmos',
    'ton',
    'other',
];
/** assets.kind — no DB CHECK constraint, kept as a convention. */
export const ASSET_KINDS = ['crypto', 'fiat'];
/* -------------------------------------------------------------------------- */
/* wallet & ledger                                                            */
/* -------------------------------------------------------------------------- */
/** ledger_entries.entry_type */
export const LEDGER_ENTRY_TYPES = [
    'deposit',
    'withdrawal',
    'withdrawal_fee',
    'withdrawal_platform_fee',
    /** The reservation a withdrawal request makes and the release a rejection gives back. */
    'withdrawal_lock',
    'withdrawal_release',
    'trade_buy',
    'trade_sell',
    'trading_fee',
    'realized_pnl',
    /** System correction that makes a signal with programmed income settle to its configured result. */
    'signal_programmed_profit',
    'funding',
    'margin_lock',
    'margin_release',
    /** Funds a resting order reserves while it waits on the book, and their return on cancel or fill. */
    'order_lock',
    'order_release',
    'liquidation',
    'transfer_in',
    'transfer_out',
    'copy_allocation',
    'copy_release',
    'copy_commission',
    'referral',
    /** A promo bonus that met its unlock conditions and reached the spot balance (negative: clawed back). */
    'bonus',
    'adjustment',
];
/**
 * Tuple form (`as const`) so `z.enum(ACCOUNT_TYPES)` and other literal-union
 * consumers can read it directly; a `readonly AccountType[]` cannot be.
 */
export const ACCOUNT_TYPES = ['spot', 'futures', 'copy'];
/** deposits.status */
export const DEPOSIT_STATUSES = [
    'detected',
    'confirming',
    'credited',
    /**
     * The transfer happened and the funds are sitting at a platform-controlled
     * address, but it is under the network asset's minimum. Recorded rather than
     * dropped so support can find it; never credited automatically.
     */
    'below_minimum',
    'orphaned',
    /** A credited deposit a reorg undid, compensated by an operator. */
    'reversed',
    'failed',
];
/** withdrawals.status */
export const WITHDRAWAL_STATUSES = [
    'pending',
    'approved',
    'rejected',
    'broadcasting',
    'sent',
    'confirmed',
    'failed',
];
/* -------------------------------------------------------------------------- */
/* markets & trading                                                          */
/* -------------------------------------------------------------------------- */
/** markets.market_type */
export const MARKET_TYPES = ['spot', 'futures'];
/** orders.side, trades.side */
export const ORDER_SIDES = ['buy', 'sell'];
/** orders.type */
export const ORDER_TYPES = [
    'market',
    'limit',
    'stop_market',
    'take_profit',
    'stop_loss',
];
/** orders.time_in_force */
export const TIME_IN_FORCES = ['GTC', 'IOC', 'FOK'];
/** orders.status */
export const ORDER_STATUSES = [
    'new',
    'open',
    'partially_filled',
    'filled',
    'cancelled',
    'rejected',
    'expired',
];
/** orders.status values that are still working on the book. */
export const OPEN_ORDER_STATUSES = ['new', 'open', 'partially_filled'];
/** orders.source, positions.source */
export const ORDER_SOURCES = ['manual', 'copy', 'liquidation', 'system'];
/** orders.margin_mode, positions.margin_mode */
export const MARGIN_MODES = ['cross', 'isolated'];
/** trades.role */
export const TRADE_ROLES = ['maker', 'taker'];
/** positions.side */
export const POSITION_SIDES = ['long', 'short'];
/** positions.status */
export const POSITION_STATUSES = ['open', 'closed', 'liquidated'];
/** candles.interval */
export const CANDLE_INTERVALS = [
    '1m',
    '5m',
    '15m',
    '30m',
    '1h',
    '4h',
    '1d',
    '1w',
];
/* -------------------------------------------------------------------------- */
/* copy trading                                                               */
/* -------------------------------------------------------------------------- */
/** master_traders.status */
export const MASTER_TRADER_STATUSES = ['pending', 'active', 'paused', 'blocked'];
/** master_stats.risk_level */
export const RISK_LEVELS = ['low', 'medium', 'high'];
/** copy_subscriptions.status */
export const COPY_SUBSCRIPTION_STATUSES = ['active', 'paused', 'stopped'];
/** copy_subscriptions.mode */
export const COPY_MODES = ['proportional', 'fixed_amount', 'fixed_multiplier'];
/** copy_subscriptions.copy_leverage_mode */
export const COPY_LEVERAGE_MODES = ['follow_master', 'fixed'];
/**
 * master_equity_daily.source — how a day's point on a master's equity curve
 * was obtained: `snapshot` is marked to market at the time it was taken
 * (capital + realised + open positions at the feed price); `backfill` was
 * rebuilt later from closed positions only, so it carries no unrealised part.
 */
export const EQUITY_POINT_SOURCES = ['snapshot', 'backfill'];
/**
 * Sharpe ratio methodology (roadmap 4.7): mean ÷ sample standard deviation of
 * the daily returns of the honest equity curve, × √365 (crypto trades every
 * day), risk-free rate 0. Constants on purpose, not operator settings — a
 * lower minimum would publish noise as a statistic.
 */
/**
 * Completed trading days (days the master held a position) needed before a
 * Sharpe figure is published. Idle days still enter the ratio as zero returns,
 * but they are no evidence, so they do not count towards this.
 */
export const SHARPE_MIN_RETURNS = 30;
/** The trailing window, in UTC days, the figure is computed over. */
export const SHARPE_WINDOW_DAYS = 365;
/** Annualisation: daily returns × √365. */
export const SHARPE_PERIODS_PER_YEAR = 365;
/** copy_trades.status */
export const COPY_TRADE_STATUSES = ['pending', 'executed', 'skipped', 'failed'];
/** copy_settlements.status */
export const COPY_SETTLEMENT_STATUSES = ['pending', 'settled', 'failed'];
/* -------------------------------------------------------------------------- */
/* websocket                                                                  */
/* -------------------------------------------------------------------------- */
/** Socket.IO namespace the api exposes. */
export const WS_NAMESPACE = '/ws';
/** Per-user room name. */
export const userRoom = (userId) => `user:${userId}`;
/**
 * Per-market room: a socket joins it while a screen shows that pair and gets
 * the pair's ticker, candles and order book. Nothing is broadcast to sockets
 * that are not looking.
 */
export const marketRoom = (symbol) => `market:${symbol}`;
/** The room that receives the batched ticker feed for every market. */
export const TICKERS_ROOM = 'tickers';
export const WS_EVENTS = {
    balanceUpdate: 'balance:update',
    depositDetected: 'deposit:detected',
    depositConfirmed: 'deposit:confirmed',
    orderUpdate: 'order:update',
    tradeExecuted: 'trade:executed',
    positionUpdate: 'position:update',
    notification: 'notification',
    tickers: 'tickers',
    marketTicker: 'market:ticker',
    marketCandle: 'market:candle',
    marketOrderBook: 'market:orderbook',
};
/** Client → server requests for market data. */
export const WS_REQUESTS = {
    subscribeMarket: 'market:subscribe',
    unsubscribeMarket: 'market:unsubscribe',
    subscribeTickers: 'tickers:subscribe',
    unsubscribeTickers: 'tickers:unsubscribe',
};
/* -------------------------------------------------------------------------- */
/* helpers                                                                    */
/* -------------------------------------------------------------------------- */
/**
 * Type guard factory for the const tuples above.
 *
 * @example
 *   const isUserRole = isOneOf(USER_ROLES);
 *   if (isUserRole(value)) { // value: UserRole }
 */
export function isOneOf(allowed) {
    const set = new Set(allowed);
    return (value) => typeof value === 'string' && set.has(value);
}
//# sourceMappingURL=enums.js.map